import {
  MINIMUM_VIDEO_COMPARISON_SELECTIONS,
  parseVideoSelectionUrl,
  type AnalyticsCaptureRequest,
} from "@meta-video/contracts";
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

import { parseComparisonViewSearch } from "../url/comparisonSearch.js";
import { subscribeToLocationSearchChanges } from "../url/locationSearch.js";
import { parseVideoWorkspaceSearch } from "../url/workspaceSearch.js";
import { useAnalytics, useAnalyticsSessionId } from "./AnalyticsProvider.js";
import { analyticsEvents } from "./events.js";

type PageviewProperties = Extract<
  AnalyticsCaptureRequest,
  { readonly event: "$pageview" }
>["properties"];

function canonicalScreen(
  pathname: string,
  search: string,
): PageviewProperties | null {
  if (pathname === "/") {
    const video = parseVideoWorkspaceSearch(search);
    if (video.kind === "valid" && video.videoView && video.video !== null) {
      return { path: "/", screen: "video-detail" };
    }
    const comparison = parseComparisonViewSearch(search);
    const selections = parseVideoSelectionUrl(search);
    if (
      comparison.kind === "valid" &&
      video.kind === "valid" &&
      !video.videoView &&
      selections.kind === "valid" &&
      selections.selections.length >= MINIMUM_VIDEO_COMPARISON_SELECTIONS
    ) {
      return { path: "/", screen: "comparison" };
    }
    return { path: "/", screen: "report" };
  }
  switch (pathname) {
    case "/saved-views":
      return { path: pathname, screen: "saved-views" };
    case "/admin":
      return { path: pathname, screen: "admin" };
    case "/admin/users":
      return { path: pathname, screen: "admin-users" };
    case "/admin/subgroups":
      return { path: pathname, screen: "admin-subgroups" };
    case "/admin/brands":
      return { path: pathname, screen: "admin-brands" };
    case "/admin/domains":
      return { path: pathname, screen: "admin-domains" };
    default:
      return null;
  }
}

export function ScreenTracker() {
  const analytics = useAnalytics();
  const sessionId = useAnalyticsSessionId();
  const location = useLocation();
  const [browserLocation, setBrowserLocation] = useState(() => ({
    pathname: window.location.pathname,
    search: window.location.search,
  }));
  const lastCaptureKeyRef = useRef<string | null>(null);

  const readBrowserLocation = () => {
    const next = {
      pathname: window.location.pathname,
      search: window.location.search,
    };
    setBrowserLocation((current) =>
      current.pathname === next.pathname && current.search === next.search
        ? current
        : next);
  };

  useEffect(() => {
    readBrowserLocation();
  }, [location.pathname, location.search]);

  useEffect(() =>
    subscribeToLocationSearchChanges(readBrowserLocation), []);

  const screen = useMemo(
    () => canonicalScreen(browserLocation.pathname, browserLocation.search),
    [browserLocation.pathname, browserLocation.search],
  );

  useEffect(() => {
    if (!sessionId || !screen) return;
    const captureKey = `${sessionId}:${screen.path}:${screen.screen}`;
    if (lastCaptureKeyRef.current === captureKey) return;
    let cancelled = false;
    queueMicrotask(() => {
      if (cancelled) return;
      const settled = canonicalScreen(
        window.location.pathname,
        window.location.search,
      );
      if (
        !settled ||
        settled.path !== screen.path ||
        settled.screen !== screen.screen
      ) return;
      lastCaptureKeyRef.current = captureKey;
      analytics.capture(analyticsEvents.pageview(sessionId, screen));
    });
    return () => {
      cancelled = true;
    };
  }, [analytics, screen, sessionId]);

  return null;
}
